import React from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';

export const ErrorFallback = ({ resetError }: { resetError: () => void }) => (
  <div className="min-h-screen flex items-center justify-center bg-muted">
    <div className="max-w-md w-full bg-background shadow-lg rounded-lg p-6">
      <div className="flex items-center justify-center w-12 h-12 mx-auto bg-destructive-subtle rounded-full">
        <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <div className="mt-3 text-center">
        <h3 className="text-lg font-medium text-foreground">Something went wrong</h3>
        <div className="mt-2 px-7 py-3">
          <p className="text-sm text-muted-foreground">
            An unexpected error has occurred. Please try refreshing the page.
          </p>
        </div>
        <div className="mt-4">
          <Button type="button" className="px-4 py-2" onClick={resetError}>
            Try again
          </Button>
        </div>
      </div>
    </div>
  </div>
);

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (props: { resetError: () => void }) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  resetError = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback({ resetError: this.resetError });
    }
    return this.props.children;
  }
}
